import React, { useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaWhatsapp, 
  FaFacebook, 
  FaInstagram, 
  FaYoutube, 
  FaShippingFast, 
  FaFileContract, 
  FaShieldAlt,
  FaEye,
  FaChevronRight
} from 'react-icons/fa';
import './footer.css';

function Footer() {
  const [contactData, setContactData] = useState(null);
  const [visitorCount, setVisitorCount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_BASE_URL}content.php`);
        const { body } = response.data;
        setContactData(body.contact);
        setVisitorCount(body.visitor_count);
      } catch (error) {
        console.error("Error fetching the contact data", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactData();
  }, []);

  if (isLoading) {
    return (
      <div className="mf-loading">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!contactData) {
    return (
      <div className="mf-loading">
        <p className="text-muted">Unable to load footer content</p>
      </div>
    );
  }

  return (
    <div className="mf-wrapper">
      {/* Main Footer */}
      <footer className="mf-footer">
        <div className="mf-container">
          <div className="mf-grid">
            {/* Address & About */}
            <div className="mf-section">
              <div className="mf-header">
                <div className="mf-header-icon">
                  <FaMapMarkerAlt />
                </div>
                <h3 className="mf-title">Our Location</h3>
              </div>
              <div className="mf-content">
                <p className="mf-address-text">
                  {contactData.address}
                </p>
                
                <div className="mf-visitor-counter">
                  <div className="mf-counter-icon">
                    <FaEye />
                  </div>
                  <div className="mf-counter-content">
                    <span className="mf-counter-label">Website Visitors</span>
                    <span className="mf-counter-value">
                      {visitorCount?.visitor_count?.toLocaleString() || '1,234'}
                    </span>
                  </div>
                </div>

                <div className="mf-social-section">
                  <h4 className="mf-social-title">Connect With Us</h4>
                  <div className="mf-social-links">
                    <a 
                      href="https://facebook.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mf-social-link facebook"
                      aria-label="Visit our Facebook page"
                    >
                      <FaFacebook />
                    </a>
                    <a 
                      href="https://instagram.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mf-social-link instagram"
                      aria-label="Visit our Instagram profile"
                    >
                      <FaInstagram />
                    </a>
                    <a 
                      href="https://youtube.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mf-social-link youtube"
                      aria-label="Visit our YouTube channel"
                    >
                      <FaYoutube />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="mf-section">
              <div className="mf-header">
                <div className="mf-header-icon">
                  <FaShieldAlt />
                </div>
                <h3 className="mf-title">Quick Links</h3>
              </div>
              <div className="mf-content">
                <div className="mf-quick-links">
                  <Link to="/shippingpolicy" className="mf-quick-link">
                    <div className="mf-link-icon">
                      <FaShippingFast />
                    </div>
                    <div className="mf-link-content">
                      <span className="mf-link-title">Shipping & Delivery</span>
                      <span className="mf-link-desc">
                        Track your orders and delivery status
                      </span>
                    </div>
                    <FaChevronRight className="text-white opacity-50" />
                  </Link>
                  
                  <Link to="/terms" className="mf-quick-link">
                    <div className="mf-link-icon">
                      <FaFileContract />
                    </div>
                    <div className="mf-link-content">
                      <span className="mf-link-title">Terms & Conditions</span>
                      <span className="mf-link-desc">
                        Read our policies and guidelines
                      </span>
                    </div>
                    <FaChevronRight className="text-white opacity-50" />
                  </Link>
                  
                  <Link to="/privacy" className="mf-quick-link">
                    <div className="mf-link-icon">
                      <FaShieldAlt />
                    </div>
                    <div className="mf-link-content">
                      <span className="mf-link-title">Privacy Policy</span>
                      <span className="mf-link-desc">
                        Your data safety and security
                      </span>
                    </div>
                    <FaChevronRight className="text-white opacity-50" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Contact & Social */}
            <div className="mf-section">
              <div className="mf-header">
                <div className="mf-header-icon">
                  <FaPhone />
                </div>
                <h3 className="mf-title">Contact Info</h3>
              </div>
              <div className="mf-content">
                <div className="mf-contact-infos">
                  <div className="mf-contact-item">
                    <div className="mf-contact-icons phone">
                      <FaPhone />
                    </div>
                    <div className="mf-contact-details">
                      <span className="mf-contact-label">Phone</span>
                      <a 
                        href={`tel:${contactData.phonenumber}`} 
                        className="mf-contact-value"
                      >
                        {contactData.phonenumber}
                      </a>
                    </div>
                  </div>
                  
                  <div className="mf-contact-item">
                    <div className="mf-contact-icons email">
                      <FaEnvelope />
                    </div>
                    <div className="mf-contact-details">
                      <span className="mf-contact-label">Email</span>
                      <a 
                        href={`mailto:${contactData.email}`} 
                        className="mf-contact-value"
                      >
                        {contactData.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="mf-contact-item">
                    <div className="mf-contact-icons whatsapp">
                      <FaWhatsapp />
                    </div>
                    <div className="mf-contact-details">
                      <span className="mf-contact-label">WhatsApp</span>
                      <a 
                        href={`https://wa.me/${contactData.whatsapp_number}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mf-contact-value"
                      >
                        {contactData.whatsapp_number}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mf-notice">
          <div className="mf-container">
            <p className="mf-notice-text">
              <strong>⚠️ Important Notice:</strong> Supreme Court has banned online sales of firecrackers from 2018. 
              We obey the order and don't permit online purchases. Products are shown for reference only. 
              Add products to Quick Purchase and submit enquiry. We'll contact you within 24 hours via WhatsApp/Phone.
            </p>
          </div>
        </div>
      </footer>

      {/* Copyright */}
      <div className="mf-copyright">
        <div className="mf-container">
          <div className="mf-copyright-content">
            <p className="mf-copyright-text">
              Copyright © {new Date().getFullYear()} 
              <strong className="mf-brand-name"> Mathi Crackers</strong>
              . All Rights Reserved. 
              <br className="d-sm-none" />
              Developed by 
              <a 
                href="https://aatheshsoft.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="mf-developer-link"
              >
                Aathesh Soft Solutions
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Footer;